import { api } from "@/lib/apiClient";
import { isAxiosError } from "axios";

export type SaleTokenType = "conditional" | "unconditional";
export type SalePaymentMethod = "cash" | "online" | "cheque" | "pay_order" | "cash_deposit";

export type SaleInstallmentPayload = {
  installment_no: number;
  due_date: string;
  amount: number;
};

export type CreateSalePayload = {
  product_id: number;
  date?: string | null;
  customer_id?: number | null;
  employee_id?: number | null;
  total_amount: number;
  token_amount?: number | null;
  token_type?: SaleTokenType | null;
  balance_amount?: number | null;
  payable_date?: string | null;
  delivery_date?: string | null;
  payment_method?: SalePaymentMethod | null;
  bank_name?: string | null;
  transaction_reference_no?: string | null;
  pay_order_no?: string | null;
  description?: string | null;
  is_installment?: boolean;
  installment_count?: number | null;
  installment_first_due_date?: string | null;
  installments?: SaleInstallmentPayload[];
};

export type UpdateSalePayload = Omit<CreateSalePayload, "product_id">;

function buildSaleBody(payload: CreateSalePayload | UpdateSalePayload) {
  return {
    date: payload.date ?? null,
    customer_id: payload.customer_id ?? null,
    employee_id: payload.employee_id ?? null,
    total_amount: payload.total_amount,
    token_amount: payload.token_amount ?? 0,
    token_type: payload.token_type ?? null,
    balance_amount: payload.balance_amount ?? null,
    payable_date: payload.payable_date ?? null,
    delivery_date: payload.delivery_date ?? null,
    payment_method: payload.payment_method ?? null,
    bank_name: payload.bank_name ?? null,
    transaction_reference_no: payload.transaction_reference_no ?? null,
    pay_order_no: payload.pay_order_no ?? null,
    description: payload.description ?? null,
    is_installment: payload.is_installment ?? false,
    installment_count: payload.installment_count ?? null,
    installment_first_due_date: payload.installment_first_due_date ?? null,
    installments: payload.installments ?? null,
  };
}

export function saleErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return "Something went wrong. Please try again.";
}

export async function createSale(payload: CreateSalePayload): Promise<{ message: string }> {
  const response = await api.post<{ message?: string }>("/api/v1/sales", {
    product_id: payload.product_id,
    ...buildSaleBody(payload),
  });
  return { message: response.data?.message || "Sale recorded successfully." };
}

export async function paySaleInstallment(
  saleId: string | number,
  installmentId: string | number,
  payload: { amount: number; payment_date: string },
): Promise<{ message: string; record?: any }> {
  const response = await api.post<{ message?: string; data?: any }>(
    `/api/v1/sales/${encodeURIComponent(String(saleId))}/installments/${encodeURIComponent(String(installmentId))}/pay`,
    {
      amount: payload.amount,
      payment_date: payload.payment_date,
    },
  );
  return { message: response.data?.message || "Installment payment recorded.", record: response.data?.data ?? null };
}

export type SaleBalancePaymentPayload = {
  amount: number;
  payment_date: string;
  payment_method?: SalePaymentMethod | null;
  bank_name?: string | null;
  transaction_reference_no?: string | null;
  pay_order_no?: string | null;
  notes?: string | null;
};

export async function paySaleBalance(
  saleId: string | number,
  payload: SaleBalancePaymentPayload,
): Promise<{ message: string; record?: any }> {
  const response = await api.post<{ message?: string; data?: any }>(
    `/api/v1/sales/${encodeURIComponent(String(saleId))}/payments`,
    {
      amount: payload.amount,
      payment_date: payload.payment_date,
      payment_method: payload.payment_method || null,
      bank_name: payload.bank_name || null,
      transaction_reference_no: payload.transaction_reference_no || null,
      pay_order_no: payload.pay_order_no || null,
      notes: payload.notes || null,
    },
  );
  return { message: response.data?.message || "Balance payment recorded.", record: response.data?.data ?? null };
}

export async function updateSale(id: string | number, payload: UpdateSalePayload): Promise<{ message: string }> {
  const response = await api.patch<{ message?: string }>(`/api/v1/sales/${id}`, buildSaleBody(payload));
  return { message: response.data?.message || "Sale updated successfully." };
}
